import { useState, useEffect } from 'react'
import { useAuth } from '@/providers/AuthProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
  CheckCircle2,
  XCircle,
  Plus,
  AlertCircle,
  Calendar,
  Clock,
  Loader2,
  Users,
  Video,
  MapPin,
  Goal,
  Search,
  ChevronLeft
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import ScheduleCollaborativeSessionDialog from '../objective-setting/ScheduleCollaborativeSessionDialog'
import { performanceService } from '@/services/api/performanceService'
import { useLookupStrategicObjectives, useLookupStrategicInitiatives, useLookupOperationalInitiatives } from '@/hooks/useStrategicFramework'
import toast from 'react-hot-toast'
import { useObjectiveEditing } from '@/hooks/useObjectiveEditing'
import { useCollaborativeSession } from '@/hooks/useCollaborativeSession'
import { useObjectiveAllocation } from '@/hooks/useObjectiveAllocation'
import ObjectiveListTab from './objective-config/ObjectiveListTab'
import ObjectiveMasterList from './objective-config/ObjectiveMasterList'
import AllocationTab from './objective-config/AllocationTab'
import ConfirmationDialogs from './objective-config/ConfirmationDialogs'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import YearEndMeetingBanner from './objective-config/YearEndMeetingBanner'
import type { ObjectiveConfigurationDialogProps, PendingObjective } from './objective-config/types'
import { filterObjectives } from './objective-config/types'

export default function ObjectiveConfigurationDialog({
  open,
  onOpenChange,
  teamMember,
  userRole = 'manager',
  inline = false,
  initialTab,
  selectedYear,
  onApprove,
  onApproveBatch,
  onReject,
  onUpdate,
  onAllocate,
  onObjectivesAdded,
  onObjectivesChange,
  onSessionChange
}: ObjectiveConfigurationDialogProps) {
  const { user } = useAuth()
  const effectiveYear = selectedYear || new Date().getFullYear()
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'rejected' | 'all' | 'allocate'>(initialTab || 'active')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedKPA, setSelectedKPA] = useState('all')
  const [expandedObjective, setExpandedObjective] = useState<number | null>(null)
  const [approvalFeedback, setApprovalFeedback] = useState<Record<number, string>>({})
  const [rejectionReason, setRejectionReason] = useState<Record<number, string>>({})
  const [showRejectDialog, setShowRejectDialog] = useState<number | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<number | null>(null)
  const [showApproveAllConfirm, setShowApproveAllConfirm] = useState(false)
  const [approvingAll, setApprovingAll] = useState(false)
  const [showWithdrawDialog, setShowWithdrawDialog] = useState<number | null>(null)
  const [processingObjective, setProcessingObjective] = useState<number | null>(null)
  const [deletingObjective, setDeletingObjective] = useState<number | null>(null)
  const [withdrawingObjective, setWithdrawingObjective] = useState<number | null>(null)

  const [fullTeamMember, setFullTeamMember] = useState<any>(null)
  const [loadingFullData, setLoadingFullData] = useState(false)

  const [isRefreshing, setIsRefreshing] = useState(false)

  const [objectiveChanges, setObjectiveChanges] = useState<Record<number, any[]>>({})
  const [loadingChanges, setLoadingChanges] = useState<Record<number, boolean>>({})

  const { data: objectivesLookup = [] } = useLookupStrategicObjectives()
  const { data: initiativesLookup = [] } = useLookupStrategicInitiatives()
  const { data: operationalLookup = [] } = useLookupOperationalInitiatives()

  const {
    collaborativeSession,
    setCollaborativeSession,
    loadingSession,
    schedulingSession,
    showScheduleModal,
    setShowScheduleModal,
    scheduleForm,
    setScheduleForm,
    canCompleteSession,
    canApproveObjectives,
    canWithdrawObjectives,
    handleScheduleSession,
    resetSessionState
  } = useCollaborativeSession({
    open,
    teamMember,
    effectiveYear,
    user,
    setFullTeamMember,
    onObjectivesChange,
    onSessionChange
  })

  const {
    editingObjective,
    editedObjectives,
    savingEdit,
    setEditedObjectives,
    getCurrentObjective,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    updateObjectiveField,
    updateKPIField,
    handleAddKPI,
    handleRemoveKPI,
    resetEditState
  } = useObjectiveEditing({
    teamMember,
    userRole,
    activeTab,
    effectiveYear,
    onUpdate,
    onObjectivesChange,
    setFullTeamMember,
    setCollaborativeSession,
    setIsRefreshing
  })

  const {
    showObjectiveWizard,
    setShowObjectiveWizard,
    allocationSource,
    setAllocationSource,
    availableTemplates,
    selectedTemplate,
    setSelectedTemplate,
    expandedTemplateObjective,
    setExpandedTemplateObjective,
    allocatingTemplate,
    handleAllocateTemplate,
    handleAllocateObjectives,
    handleObjectivesCopied,
    resetAllocationState
  } = useObjectiveAllocation({
    teamMember,
    effectiveYear,
    onAllocate,
    onObjectivesAdded,
    onObjectivesChange,
    setFullTeamMember,
    setActiveTab: setActiveTab as (tab: string) => void,
    managerId: userRole === 'manager' ? user?.employeeId : undefined,
    strategicObjectivesLookup: objectivesLookup,
    strategicInitiativesLookup: initiativesLookup,
    operationalInitiativesLookup: operationalLookup,
  })

  // Fetch change history when objective is expanded
  useEffect(() => {
    const fetchObjectiveChanges = async () => {
      if (expandedObjective && activeTab === 'pending') {
        try {
          setLoadingChanges(prev => ({ ...prev, [expandedObjective]: true }))
          const changes = await performanceService.getObjectiveChanges(expandedObjective)
          setObjectiveChanges(prev => ({ ...prev, [expandedObjective]: changes || [] }))
        } catch (error) {
          console.error('Failed to fetch objective changes:', error)
          setObjectiveChanges(prev => ({ ...prev, [expandedObjective]: [] }))
        } finally {
          setLoadingChanges(prev => ({ ...prev, [expandedObjective]: false }))
        }
      }
    }

    fetchObjectiveChanges()
  }, [expandedObjective, activeTab])

  // Fetch full employee objectives when dialog opens
  useEffect(() => {
    if (open && teamMember) {
      const fetchFullObjectives = async () => {
        try {
          setLoadingFullData(true)
          const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })

          const allObjectives = data.objectives || []

          setFullTeamMember({
            ...teamMember,
            pending_objectives: allObjectives.filter((obj: any) => obj.status === 'pending_approval'),
            active_objectives: allObjectives.filter((obj: any) => obj.status === 'active'),
            rejected_objectives: allObjectives.filter((obj: any) => obj.status === 'rejected')
          })
        } catch (error) {
          console.error('Error fetching full objectives:', error)
          setFullTeamMember(teamMember)
        } finally {
          setLoadingFullData(false)
        }
      }
      fetchFullObjectives()
    }
  }, [open, teamMember])

  // Reset state when dialog closes or set initial tab when opening
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSelectedKPA('all')
      setExpandedObjective(null)
      setApprovalFeedback({})
      setRejectionReason({})
      setShowRejectDialog(null)
      setProcessingObjective(null)
      resetEditState()
      resetAllocationState()
      resetSessionState()
    } else {
      if (initialTab) {
        setActiveTab(initialTab)
      }
    }
  }, [open, initialTab])

  const handleApproveObjective = async (objectiveId: number) => {
    if (userRole === 'manager' && !canApproveObjectives) {
      const status = collaborativeSession?.status
      if (collaborativeSession?.held_with_year_end_checkin) {
        toast.error(
          `These objectives are agreed in the ${effectiveYear - 1} year-end check-in. You can approve them once it starts.`,
          { duration: 5000 }
        )
      } else if (!status) {
        toast.error(
          'Cannot approve objectives - no collaborative session exists. Please schedule and complete a meeting first.',
          { duration: 5000 }
        )
      } else if (status === 'pending_scheduling' || status === 'pending-scheduling') {
        toast.error(
          'Cannot approve objectives - the collaborative session has not been scheduled yet. Please schedule and complete a meeting first.',
          { duration: 5000 }
        )
      } else if (status === 'scheduled') {
        toast.error(
          'Approvals open once the scheduled meeting time arrives.',
          { duration: 5000 }
        )
      } else {
        toast.error(
          'Approvals open once the collaborative session has started.',
          { duration: 5000 }
        )
      }
      return
    }

    setProcessingObjective(objectiveId)
    try {
      await onApprove(objectiveId, approvalFeedback[objectiveId], null, teamMember?.employee_id)

      setApprovalFeedback(prev => {
        const next = { ...prev }
        delete next[objectiveId]
        return next
      })
    } finally {
      setProcessingObjective(null)
    }
  }

  // Approve every pending objective in one go (confirmed via dialog). Snapshot the
  // list up-front since approval moves items out of pending_objectives.
  const handleApproveAll = async () => {
    const pending = [...(displayMember.pending_objectives || [])]
    if (pending.length === 0) return
    setApprovingAll(true)
    try {
      if (onApproveBatch) {
        // Fast path: one round of concurrent approvals + a SINGLE refresh, instead
        // of a full team reload after each objective.
        const items = pending.map(obj => ({ id: obj.id, feedback: approvalFeedback[obj.id] }))
        const { approved, failed } = await onApproveBatch(items, teamMember?.employee_id)
        if (approved > 0) toast.success(`Approved ${approved} objective${approved === 1 ? '' : 's'}`)
        if (failed > 0) toast.error(`${failed} couldn't be approved. Please retry the rest.`)
      } else {
        // Fallback: sequential per-item approve (each settles before the next).
        let approved = 0
        try {
          for (const obj of pending) {
            await onApprove(obj.id, approvalFeedback[obj.id], null, teamMember?.employee_id)
            approved += 1
          }
          toast.success(`Approved ${approved} objective${approved === 1 ? '' : 's'}`)
        } catch (error) {
          console.error('Approve-all failed partway:', error)
          toast.error(`Approved ${approved} of ${pending.length}. Please retry the rest.`)
        }
      }
    } finally {
      setApprovalFeedback({})
      setApprovingAll(false)
      setShowApproveAllConfirm(false)
    }
  }

  const handleRejectObjective = async (objectiveId: number) => {
    if (userRole === 'manager' && !canApproveObjectives) {
      const status = collaborativeSession?.status
      if (collaborativeSession?.held_with_year_end_checkin) {
        toast.error(
          `These objectives are agreed in the ${effectiveYear - 1} year-end check-in. You can reject them once it starts.`,
          { duration: 5000 }
        )
      } else if (!status) {
        toast.error(
          'Cannot reject objectives - no collaborative session exists. Please schedule and complete a meeting first.',
          { duration: 5000 }
        )
      } else if (status === 'pending_scheduling' || status === 'pending-scheduling') {
        toast.error(
          'Cannot reject objectives - the collaborative session has not been scheduled yet. Please schedule and complete a meeting first.',
          { duration: 5000 }
        )
      } else if (status === 'scheduled') {
        toast.error(
          'Check-ins open once the scheduled meeting time arrives.',
          { duration: 5000 }
        )
      } else {
        toast.error(
          'Check-ins open once the collaborative session has started.',
          { duration: 5000 }
        )
      }
      return
    }

    if (!rejectionReason[objectiveId] || rejectionReason[objectiveId].trim().length < 10) {
      return
    }

    setProcessingObjective(objectiveId)
    try {
      await onReject(objectiveId, rejectionReason[objectiveId])
      setRejectionReason(prev => {
        const next = { ...prev }
        delete next[objectiveId]
        return next
      })
      setShowRejectDialog(null)
    } finally {
      setProcessingObjective(null)
    }
  }

  const handleDeleteObjective = async (objectiveId: number) => {
    setDeletingObjective(objectiveId)
    try {
      await performanceService.deleteObjective(objectiveId)
      toast.success('Objective deleted and moved to History tab')
      setShowDeleteDialog(null)
      setExpandedObjective(null)

      if (teamMember) {
        try {
          const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })

          const updatedMember = {
            ...teamMember,
            pending_objectives: data.objectives.filter((obj: any) => obj.status === 'pending_approval'),
            active_objectives: data.objectives.filter((obj: any) => obj.status === 'active'),
            rejected_objectives: data.objectives.filter((obj: any) => obj.status === 'rejected')
          }
          setFullTeamMember(updatedMember)
        } catch (err) {
          console.error('Error refreshing objectives:', err)
        }
      }

      if (onObjectivesChange) {
        await onObjectivesChange()
      }
    } catch (error) {
      console.error('Failed to delete objective:', error)
      toast.error('Failed to delete objective')
    } finally {
      setDeletingObjective(null)
    }
  }

  const handleWithdrawObjective = async (objectiveId: number) => {
    setWithdrawingObjective(objectiveId)
    try {
      await performanceService.withdrawObjective(objectiveId, userRole === 'manager' ? 'manager' : 'employee')
      toast.success('Objective withdrawn successfully')
      setShowWithdrawDialog(null)
      setExpandedObjective(null)

      if (teamMember) {
        try {
          const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })

          const updatedMember = {
            ...teamMember,
            pending_objectives: data.objectives.filter((obj: any) => obj.status === 'pending_approval'),
            active_objectives: data.objectives.filter((obj: any) => obj.status === 'active'),
            rejected_objectives: data.objectives.filter((obj: any) => obj.status === 'rejected')
          }
          setFullTeamMember(updatedMember)
        } catch (err) {
          console.error('Error refreshing objectives:', err)
        }
      }

      if (onObjectivesChange) {
        await onObjectivesChange()
      }
    } catch (error: any) {
      console.error('Failed to withdraw objective:', error)
      const detail = error?.message || 'Failed to withdraw objective'
      toast.error(detail)
    } finally {
      setWithdrawingObjective(null)
    }
  }

  if (!teamMember) return null

  const displayMember = fullTeamMember || teamMember

  // Shared props for ObjectiveListTab
  const listTabProps = {
    expandedObjective,
    setExpandedObjective: (id: number | null) => setExpandedObjective(id),
    editingObjective,
    editedObjectives,
    savingEdit,
    processingObjective,
    deletingObjective,
    withdrawingObjective,
    canApproveObjectives,
    canWithdrawObjectives,
    userRole,
    approvalFeedback,
    setApprovalFeedback,
    objectiveChanges,
    loadingChanges,
    collaborativeSession,
    year: effectiveYear,
    objectivesLookup,
    initiativesLookup,
    operationalLookup,
    displayMemberName: displayMember.display_name,
    getCurrentObjective,
    onStartEdit: handleStartEdit,
    onCancelEdit: handleCancelEdit,
    onSaveEdit: handleSaveEdit,
    onUpdateField: updateObjectiveField,
    onUpdateKPIField: (objectiveId: number, kpiId: string, field: string, value: any) => updateKPIField(objectiveId, kpiId, field as any, value),
    onAddKPI: handleAddKPI,
    onRemoveKPI: handleRemoveKPI,
    onSetEditedObjectives: setEditedObjectives,
    onApprove: handleApproveObjective,
    onShowRejectDialog: setShowRejectDialog,
    onShowDeleteDialog: setShowDeleteDialog,
    onShowWithdrawDialog: setShowWithdrawDialog
  }

  // ── Master-detail: objectives for the active list tab + the selected one ──
  const tabObjectives: PendingObjective[] =
    activeTab === 'pending' ? filterObjectives(displayMember.pending_objectives, searchQuery, selectedKPA) :
    activeTab === 'active' ? filterObjectives(displayMember.active_objectives || [], searchQuery, selectedKPA) :
    activeTab === 'rejected' ? filterObjectives(displayMember.rejected_objectives || [], searchQuery, selectedKPA) :
    activeTab === 'all' ? [
      ...filterObjectives(displayMember.pending_objectives, searchQuery, selectedKPA),
      ...filterObjectives(displayMember.active_objectives || [], searchQuery, selectedKPA),
      ...filterObjectives(displayMember.rejected_objectives || [], searchQuery, selectedKPA),
    ] : []
  const selectedObjective = tabObjectives.find((o: PendingObjective) => o.id === expandedObjective) || null
  const detailTabType = (activeTab === 'allocate' ? 'all' : activeTab) as 'pending' | 'active' | 'rejected' | 'all'
  const masterEmptyLabel =
    activeTab === 'pending' ? `No objectives pending approval for ${displayMember.display_name}.` :
    activeTab === 'active' ? `No active objectives for ${displayMember.display_name}.` :
    activeTab === 'rejected' ? `No rejection history for ${displayMember.display_name}.` :
    `${displayMember.display_name} has no objectives yet.`

  const content = (
    <div className={`relative ${inline ? "w-full bg-white rounded-lg border shadow-sm" : "h-full w-full overflow-hidden flex flex-col"}`}>
      {/* Refreshing overlay */}
      <AnimatePresence>
        {isRefreshing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-50 flex items-center justify-center"
          >
            <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-lg border">
              <Loader2 className="h-5 w-5 animate-spin text-heritage-blue" />
              <span className="text-sm font-medium text-gray-700">Updating objectives...</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`px-4 py-3 sm:px-6 sm:py-4 border-b ${inline ? 'bg-white' : 'bg-gradient-to-br from-heritage-blue to-future-blue'} flex-shrink-0`}>
        <h2 className={`text-base sm:text-2xl font-bold ${inline ? 'text-gray-900' : 'text-white'} flex items-center gap-2 sm:gap-3 min-w-0`}>
          <Goal className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
          <span className="truncate">{inline ? 'Objectives' : `${displayMember.display_name} — Objectives`}</span>
          <Badge
            variant="secondary"
            className="shrink-0 text-xs sm:text-sm"
            title={`Objectives for ${effectiveYear}`}
            data-testid="objectives-year"
          >
            {effectiveYear}
          </Badge>
        </h2>
        <p className={`truncate text-xs sm:text-base ${inline ? 'text-gray-600 mt-0.5 sm:mt-1' : 'text-blue-50 mt-0.5'}`}>
          {displayMember.job_title} • {displayMember.campus} • {displayMember.functional_area || displayMember.department}
        </p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {/* Collaborative Session Status Banner - Manager view */}
        {userRole === 'manager' && (
          displayMember.pending_objectives.length > 0 ||
          displayMember.active_objectives?.length > 0 ||
          displayMember.rejected_objectives?.length > 0
        ) && (
          <div className="px-4 pt-3 sm:px-6 sm:pt-4 flex-shrink-0">
            {loadingSession ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="text-sm text-gray-600">Loading session status...</span>
              </div>
            ) : collaborativeSession?.held_with_year_end_checkin ? (
              <YearEndMeetingBanner
                session={collaborativeSession}
                year={effectiveYear}
                memberName={displayMember.display_name}
                audience="manager"
              />
            ) : !collaborativeSession ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Users className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">Collaborative Session Required</h4>
                    <p className="hidden sm:block text-sm text-amber-700 mt-1">
                      Before approving objectives or making changes, you should schedule a collaborative discussion with {displayMember.display_name}.
                    </p>
                    <Button
                      size="sm"
                      className="mt-2 sm:mt-3 bg-amber-600 hover:bg-amber-700"
                      onClick={() => setShowScheduleModal(true)}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule Meeting
                    </Button>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'pending_scheduling' || collaborativeSession.status === 'pending-scheduling' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">Session Pending Scheduling</h4>
                    <p className="hidden sm:block text-sm text-amber-700 mt-1">
                      Objectives have been created or updated. Please schedule a meeting to discuss with {displayMember.display_name}.
                    </p>
                    <Button
                      size="sm"
                      className="mt-2 sm:mt-3 bg-amber-600 hover:bg-amber-700"
                      onClick={() => setShowScheduleModal(true)}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule Meeting
                    </Button>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'in-progress' || collaborativeSession.status === 'in_progress' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Users className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-900">Session In Progress</h4>
                    <p className="text-sm text-emerald-700 mt-1">
                      Collaborative session is in progress. You can review the objectives and approve them now.
                    </p>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'scheduled' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Meeting Scheduled</h4>
                    <div className="text-sm text-blue-700 mt-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {collaborativeSession.scheduled_date
                            ? new Date(collaborativeSession.scheduled_date).toLocaleString('en-ZA', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Date not set'}
                        </span>
                      </div>
                      {collaborativeSession.meeting_location && (
                        <div className="flex items-center gap-2">
                          {collaborativeSession.meeting_type === 'virtual' ? (
                            <Video className="h-4 w-4" />
                          ) : (
                            <MapPin className="h-4 w-4" />
                          )}
                          <span>{collaborativeSession.meeting_location}</span>
                        </div>
                      )}
                    </div>
                    {canCompleteSession ? (
                      <p className="text-sm text-blue-600 mt-2">
                        Meeting time has arrived — you can review and approve the objectives now.
                      </p>
                    ) : (
                      <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Objective approvals open once the scheduled meeting time arrives.
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          const schedDate = new Date(collaborativeSession.scheduled_date)
                          const dateStr = schedDate.toISOString().split('T')[0]
                          const timeStr = schedDate.toTimeString().slice(0, 5)

                          setScheduleForm({
                            date: dateStr,
                            time: timeStr,
                            duration: '60',
                            location: collaborativeSession.meeting_location || '',
                            meetingType: collaborativeSession.meeting_type === 'face_to_face' ? 'face-to-face' : 'virtual',
                            agenda: collaborativeSession.meeting_agenda || ''
                          })
                          setShowScheduleModal(true)
                        }}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Reschedule
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'completed' && displayMember.pending_objectives.length > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">
                      {displayMember.pending_objectives.length} Pending Objective{displayMember.pending_objectives.length > 1 ? 's' : ''} Awaiting Approval
                    </h4>
                    <p className="hidden sm:block text-sm text-amber-700 mt-1">
                      Your collaborative session is complete. Please review and approve the pending objectives.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {userRole === 'manager' && canApproveObjectives && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setShowApproveAllConfirm(true)}
                          disabled={approvingAll}
                        >
                          {approvingAll ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                          )}
                          Approve All ({displayMember.pending_objectives.length})
                        </Button>
                      )}
                      {activeTab !== 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-800 hover:bg-amber-100"
                          onClick={() => setActiveTab('pending')}
                        >
                          Review individually
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Collaborative Session Status Banner - Employee view */}
        {userRole === 'employee' && (
          displayMember.pending_objectives.length > 0 ||
          displayMember.active_objectives?.length > 0
        ) && (
          <div className="px-4 pt-3 sm:px-6 sm:pt-4 flex-shrink-0">
            {loadingSession ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="text-sm text-gray-600">Loading session status...</span>
              </div>
            ) : collaborativeSession?.held_with_year_end_checkin ? (
              <YearEndMeetingBanner
                session={collaborativeSession}
                year={effectiveYear}
                memberName={displayMember.display_name}
                audience="employee"
              />
            ) : !collaborativeSession ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Awaiting Collaborative Session</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Your people leader will schedule a collaborative meeting to discuss your objectives before they can be approved.
                    </p>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'pending_scheduling' || collaborativeSession.status === 'pending-scheduling' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">Session Pending Scheduling</h4>
                    <p className="hidden sm:block text-sm text-amber-700 mt-1">
                      Your objectives are awaiting discussion. Your people leader will schedule a meeting soon.
                    </p>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'scheduled' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Meeting Scheduled</h4>
                    <div className="text-sm text-blue-700 mt-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {collaborativeSession.scheduled_date
                            ? new Date(collaborativeSession.scheduled_date).toLocaleString('en-ZA', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Date not set'}
                        </span>
                      </div>
                      {collaborativeSession.meeting_location && (
                        <div className="flex items-center gap-2">
                          {collaborativeSession.meeting_type === 'virtual' ? (
                            <Video className="h-4 w-4" />
                          ) : (
                            <MapPin className="h-4 w-4" />
                          )}
                          <span>{collaborativeSession.meeting_location}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-blue-600 mt-2">
                      Please prepare for this collaborative session to discuss and finalise your objectives.
                    </p>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'in-progress' || collaborativeSession.status === 'in_progress' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Users className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-900">Session In Progress</h4>
                    <p className="text-sm text-emerald-700 mt-1">
                      Your collaborative session is currently in progress. Objectives will be finalised once your people leader marks the session as complete.
                    </p>
                  </div>
                </div>
              </div>
            ) : collaborativeSession.status === 'completed' && displayMember.pending_objectives.length > 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-900">Session Completed</h4>
                    <p className="text-sm text-green-700 mt-1">
                      Your collaborative session is complete. Your people leader will now review and approve your pending objectives.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'active' | 'rejected' | 'all' | 'allocate')} className="flex-1 min-h-0 flex flex-col">
          <div className="px-4 pt-3 sm:px-6 sm:pt-4 flex-shrink-0">
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search objectives..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedKPA} onValueChange={setSelectedKPA}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All KPAs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All KPAs</SelectItem>
                  {(() => {
                    const uniqueKPAs = new Set<string>()
                    displayMember.pending_objectives.forEach((obj: any) => obj.kpa?.display_name && uniqueKPAs.add(obj.kpa.display_name))
                    displayMember.active_objectives?.forEach((obj: any) => obj.kpa?.display_name && uniqueKPAs.add(obj.kpa.display_name))
                    displayMember.rejected_objectives?.forEach((obj: any) => obj.kpa?.display_name && uniqueKPAs.add(obj.kpa.display_name))
                    return Array.from(uniqueKPAs).map(kpa => (
                      <SelectItem key={kpa} value={kpa}>{kpa}</SelectItem>
                    ))
                  })()}
                </SelectContent>
              </Select>
            </div>

            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 text-xs sm:text-sm">
              <TabsTrigger value="all" className="gap-2">
                <Goal className="h-4 w-4" />
                All
                <Badge variant="secondary" className="ml-1">
                  {(displayMember.pending_objectives?.length || 0) + (displayMember.active_objectives?.length || 0) + (displayMember.rejected_objectives?.length || 0)}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="pending" className="gap-2">
                <AlertCircle className="h-4 w-4" />
                Pending
                {displayMember.pending_objectives.length > 0 && (
                  <Badge variant="destructive" className="ml-1">
                    {displayMember.pending_objectives.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="active" className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Active
                {displayMember.active_objectives?.length > 0 && (
                  <Badge className="ml-1 bg-emerald-100 text-emerald-800">
                    {displayMember.active_objectives.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected" className="gap-2">
                <XCircle className="h-4 w-4" />
                History
                {displayMember.rejected_objectives?.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {displayMember.rejected_objectives.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="allocate"
                className="gap-2"
                id="tour-add-new-tab"
                data-tour="add-new-tab"
              >
                <Plus className="h-4 w-4" />
                Add New
              </TabsTrigger>
            </TabsList>
          </div>

          {activeTab === 'allocate' ? (
            <ScrollArea className="flex-1 h-full">
              <div className="px-4 sm:px-6 pb-6 pt-4">
                <AllocationTab
                  teamMember={teamMember}
                  year={effectiveYear}
                  onObjectivesCopied={handleObjectivesCopied}
                  allocationSource={allocationSource}
                  setAllocationSource={setAllocationSource}
                  showObjectiveWizard={showObjectiveWizard}
                  setShowObjectiveWizard={setShowObjectiveWizard}
                  availableTemplates={availableTemplates}
                  selectedTemplate={selectedTemplate}
                  setSelectedTemplate={setSelectedTemplate}
                  expandedTemplateObjective={expandedTemplateObjective}
                  setExpandedTemplateObjective={setExpandedTemplateObjective}
                  allocatingTemplate={allocatingTemplate}
                  onAllocateTemplate={handleAllocateTemplate}
                  onAllocateObjectives={handleAllocateObjectives}
                />
              </div>
            </ScrollArea>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col md:flex-row">
              {/* Master: compact, selectable objective list */}
              <div className={`min-h-0 md:w-80 lg:w-96 md:border-r ${selectedObjective ? 'hidden md:flex md:flex-col' : 'flex flex-col'}`}>
                <ScrollArea className="flex-1 h-full">
                  <ObjectiveMasterList
                    objectives={tabObjectives}
                    selectedId={expandedObjective}
                    onSelect={(id) => setExpandedObjective(id)}
                    emptyLabel={masterEmptyLabel}
                  />
                </ScrollArea>
              </div>

              {/* Detail: full view + editor for the selected objective */}
              <div className={`min-h-0 flex-1 ${selectedObjective ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
                {selectedObjective ? (
                  <>
                    <div className="md:hidden px-2 py-1.5 border-b flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setExpandedObjective(null)} className="text-heritage-blue">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back to list
                      </Button>
                    </div>
                    <ScrollArea className="flex-1 h-full">
                      <div className="px-4 sm:px-6 py-4">
                        <ObjectiveListTab
                          tabType={detailTabType}
                          objectives={[selectedObjective]}
                          {...listTabProps}
                          setExpandedObjective={() => {}}
                        />
                      </div>
                    </ScrollArea>
                  </>
                ) : (
                  <div className="flex-1 hidden md:flex items-center justify-center text-center text-muted-foreground p-8">
                    <div>
                      <Goal className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
                      <p className="text-sm max-w-[16rem]">Select an objective from the list to view and edit it.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Tabs>
      </div>

      {!inline && (
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      )}

      <ConfirmationDialogs
        showRejectDialog={showRejectDialog}
        setShowRejectDialog={setShowRejectDialog}
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        showWithdrawDialog={showWithdrawDialog}
        setShowWithdrawDialog={setShowWithdrawDialog}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        processingObjective={processingObjective}
        deletingObjective={deletingObjective}
        withdrawingObjective={withdrawingObjective}
        onRejectObjective={handleRejectObjective}
        onDeleteObjective={handleDeleteObjective}
        onWithdrawObjective={handleWithdrawObjective}
      />

      {/* Approve All confirmation */}
      <ConfirmDialog
        isOpen={showApproveAllConfirm}
        onClose={() => setShowApproveAllConfirm(false)}
        onConfirm={handleApproveAll}
        type="success"
        title="Approve all pending objectives?"
        message={`This will approve and activate all ${displayMember.pending_objectives.length} pending objective${displayMember.pending_objectives.length === 1 ? '' : 's'} for ${displayMember.display_name}. This can't be undone in bulk.`}
        confirmText={`Approve all (${displayMember.pending_objectives.length})`}
        confirmButtonClass="bg-emerald-600 hover:bg-emerald-700"
      />

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <ScheduleCollaborativeSessionDialog
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          employeeName={displayMember.display_name}
          employeeId={displayMember.employee_id}
          managerId={user?.employeeId || collaborativeSession?.manager_id || 'manager'}
          isRescheduling={!!collaborativeSession?.scheduled_date}
          existingScheduledDate={collaborativeSession?.scheduled_date || null}
          onSchedule={handleScheduleSession}
        />
      )}
    </div>
  )

  if (inline) {
    return content
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-[100dvh] max-w-none rounded-none border-0 p-0 gap-0 overflow-hidden flex flex-col sm:w-[96vw] sm:max-w-[1800px] sm:h-[94vh] sm:max-h-[94vh] sm:rounded-lg sm:border">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Objective Configuration</DialogTitle>
            <DialogDescription>
              Manage and configure objectives for {teamMember?.display_name || 'team member'}
            </DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        {content}
      </DialogContent>
    </Dialog>
  )
}
